import { useState, useRef, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchProjectImages, createProjectImage, updateProjectImage, deleteProjectImage, reorderProjectImages } from '../api/projectImagesApi'
import { fetchUnitTypes } from '@/features/units/api/unitTypesApi'
import { supabase } from '@/lib/supabaseClient'

export default function ProjectImagesManager({ projectId, developerId }) {
  const queryClient = useQueryClient()
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [editingImage, setEditingImage] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [draggedThumbIndex, setDraggedThumbIndex] = useState(null)
  const [uploadImageType, setUploadImageType] = useState('photo')
  const [uploadFloorLevel, setUploadFloorLevel] = useState('')
  const fileInputRef = useRef(null)

  const { data: imagesData } = useQuery({
    queryKey: ['project-images', projectId],
    queryFn: () => fetchProjectImages(projectId),
  })

  const { data: unitTypesData } = useQuery({
    queryKey: ['unit-types', projectId],
    queryFn: () => fetchUnitTypes(projectId),
  })

  const images = imagesData?.data || []
  const unitTypes = unitTypesData?.data || []
  const selectedImage = images[selectedIndex]

  const createMutation = useMutation({
    mutationFn: createProjectImage,
    onSuccess: () => {
      queryClient.invalidateQueries(['project-images', projectId])
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }) => updateProjectImage(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries(['project-images', projectId])
      setEditingImage(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (imageId) => {
      const image = images.find(img => img.id === imageId)
      if (image) {
        const pathMatch = image.image_url.match(/project-images\/(.+)$/)
        if (pathMatch) {
          await supabase.storage
            .from('project-images')
            .remove([pathMatch[1]])
        }
      }
      return deleteProjectImage(imageId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['project-images', projectId])
      if (selectedIndex >= images.length - 1 && selectedIndex > 0) {
        setSelectedIndex(selectedIndex - 1)
      }
    },
  })

  const reorderMutation = useMutation({
    mutationFn: ({ projectId, imageIds }) => reorderProjectImages(projectId, imageIds),
    onSuccess: () => {
      queryClient.invalidateQueries(['project-images', projectId])
    },
  })

  const uploadFiles = async (files) => {
    if (files.length === 0) return

    setUploading(true)

    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) continue

        const fileExt = file.name.split('.').pop()
        const fileName = `${developerId}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`

        const { error: uploadError } = await supabase.storage
          .from('project-images')
          .upload(fileName, file)

        if (uploadError) throw uploadError

        const { data: { publicUrl } } = supabase.storage
          .from('project-images')
          .getPublicUrl(fileName)

        const nextOrder = images.length > 0
          ? Math.max(...images.map(img => img.display_order)) + 1
          : 0

        await createMutation.mutateAsync({
          project_id: projectId,
          image_url: publicUrl,
          display_order: nextOrder,
          image_type: uploadImageType,
          floor_level: uploadImageType === 'floor_plan' ? uploadFloorLevel || null : null,
        })
      }
    } catch (error) {
      console.error('Upload error:', error)
      alert('Failed to upload images. Please try again.')
    } finally {
      setUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleFileSelect = (e) => {
    uploadFiles(Array.from(e.target.files))
  }

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setDragOver(false)
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'))
    uploadFiles(files)
  }, [images, projectId, developerId])

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    setDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    setDragOver(false)
  }, [])

  const handleThumbDragStart = (index) => {
    setDraggedThumbIndex(index)
  }

  const handleThumbDragOver = (e, index) => {
    e.preventDefault()
    if (draggedThumbIndex === null || draggedThumbIndex === index) return

    const newImages = [...images]
    const draggedItem = newImages[draggedThumbIndex]
    newImages.splice(draggedThumbIndex, 1)
    newImages.splice(index, 0, draggedItem)

    const imageIds = newImages.map(img => img.id)
    reorderMutation.mutate({ projectId, imageIds })

    if (selectedIndex === draggedThumbIndex) {
      setSelectedIndex(index)
    } else if (selectedIndex === index) {
      setSelectedIndex(draggedThumbIndex)
    }

    setDraggedThumbIndex(index)
  }

  const handleThumbDragEnd = () => {
    setDraggedThumbIndex(null)
  }

  const handleDelete = (imageId) => {
    if (confirm('Delete this image? This action cannot be undone.')) {
      deleteMutation.mutate(imageId)
    }
  }

  const handleSetPrimary = (index) => {
    if (index === 0) return
    const newImages = [...images]
    const [movedImage] = newImages.splice(index, 1)
    newImages.unshift(movedImage)
    const imageIds = newImages.map(img => img.id)
    reorderMutation.mutate({ projectId, imageIds })
    setSelectedIndex(0)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-secondary-900">Project Images</h2>
          <p className="text-sm text-secondary-600 mt-1">
            {images.length} image{images.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {images.length === 0 ? (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`card p-12 text-center border-2 border-dashed transition-all cursor-pointer ${
            dragOver
              ? 'border-primary-500 bg-primary-50'
              : 'border-secondary-300 hover:border-primary-400 hover:bg-secondary-50'
          }`}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className={`mb-4 ${dragOver ? 'text-primary-500' : 'text-secondary-400'}`}>
            <svg
              className="w-16 h-16 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
          </div>
          <h4 className="text-lg font-semibold text-secondary-900 mb-2">
            {dragOver ? 'Drop images here' : 'Add project images'}
          </h4>
          <p className="text-secondary-600 mb-4">
            Drag and drop images here, or click to browse
          </p>
          <div className="space-y-3 max-w-md mx-auto">
            <div>
              <label className="label text-sm">Image Type</label>
              <select
                value={uploadImageType}
                onChange={(e) => setUploadImageType(e.target.value)}
                className="input w-full"
              >
                <option value="photo">Photo (Regular Project Images)</option>
                <option value="scheme">Development Scheme</option>
                <option value="floor_plan">Floor Plan</option>
              </select>
            </div>
            {uploadImageType === 'floor_plan' && (
              <div>
                <label className="label text-sm">Floor Level</label>
                <input
                  type="text"
                  value={uploadFloorLevel}
                  onChange={(e) => setUploadFloorLevel(e.target.value)}
                  placeholder="e.g., Ground Floor, Level 1, Level 2"
                  className="input w-full"
                />
              </div>
            )}
            <button
              disabled={uploading}
              className="btn-primary px-6 py-2 w-full"
            >
              {uploading ? 'Uploading...' : 'Browse Files'}
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="card overflow-hidden">
            <div className="relative">
              <img
                src={selectedImage?.image_url}
                alt={selectedImage?.description || `Project image ${selectedIndex + 1}`}
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />

              <div className="absolute top-4 left-4 flex gap-2">
                <span className="bg-black bg-opacity-60 text-white px-3 py-1.5 rounded-lg text-sm font-medium">
                  {selectedIndex + 1} / {images.length}
                </span>
                {selectedIndex === 0 && (
                  <span className="bg-primary-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium">
                    Primary
                  </span>
                )}
              </div>

              <div className="absolute top-4 right-4 flex gap-2">
                {selectedIndex !== 0 && (
                  <button
                    onClick={() => handleSetPrimary(selectedIndex)}
                    className="bg-white text-secondary-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-secondary-100 transition-colors shadow-lg"
                    title="Set as primary image"
                  >
                    Set as Primary
                  </button>
                )}
                <button
                  onClick={() => setEditingImage(selectedImage)}
                  className="bg-white text-secondary-700 p-2 rounded-lg hover:bg-secondary-100 transition-colors shadow-lg"
                  title="Edit description"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleDelete(selectedImage?.id)}
                  className="bg-red-600 text-white p-2 rounded-lg hover:bg-red-700 transition-colors shadow-lg"
                  title="Delete image"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>

              {images.length > 1 && (
                <>
                  <button
                    onClick={() => setSelectedIndex(prev => prev > 0 ? prev - 1 : images.length - 1)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 p-3 rounded-full shadow-lg transition-all"
                  >
                    <svg className="w-6 h-6 text-secondary-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setSelectedIndex(prev => prev < images.length - 1 ? prev + 1 : 0)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 p-3 rounded-full shadow-lg transition-all"
                  >
                    <svg className="w-6 h-6 text-secondary-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}
            </div>

            {selectedImage && (
              <div className="p-4 bg-secondary-50 border-t border-secondary-200">
                {editingImage?.id === selectedImage.id ? (
                  <div className="space-y-3">
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <label className="label text-xs">Image Type</label>
                        <select
                          value={editingImage.image_type || 'photo'}
                          onChange={(e) => setEditingImage({ ...editingImage, image_type: e.target.value })}
                          className="input w-full"
                        >
                          <option value="photo">Photo</option>
                          <option value="scheme">Development Scheme</option>
                          <option value="floor_plan">Floor Plan</option>
                        </select>
                      </div>
                      {editingImage.image_type === 'floor_plan' && (
                        <div>
                          <label className="label text-xs">Floor Level</label>
                          <input
                            type="text"
                            value={editingImage.floor_level || ''}
                            onChange={(e) => setEditingImage({ ...editingImage, floor_level: e.target.value })}
                            placeholder="e.g., Ground Floor, Level 1"
                            className="input w-full"
                          />
                        </div>
                      )}
                      <div>
                        <label className="label text-xs">Unit Type (Optional)</label>
                        <select
                          value={editingImage.unit_type_id || ''}
                          onChange={(e) => setEditingImage({ ...editingImage, unit_type_id: e.target.value || null })}
                          className="input w-full"
                        >
                          <option value="">General (All Units)</option>
                          {unitTypes.map((type) => (
                            <option key={type.id} value={type.id}>
                              Type {type.label} ({type.bedrooms}bd/{type.bathrooms}ba)
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="label text-xs">Description</label>
                      <input
                        type="text"
                        value={editingImage.description || ''}
                        onChange={(e) => setEditingImage({ ...editingImage, description: e.target.value })}
                        placeholder="Add a description..."
                        className="input w-full"
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => setEditingImage(null)}
                        className="btn-outline px-4 py-2"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => updateMutation.mutate({
                          id: editingImage.id,
                          updates: {
                            description: editingImage.description,
                            unit_type_id: editingImage.unit_type_id,
                            image_type: editingImage.image_type,
                            floor_level: editingImage.image_type === 'floor_plan' ? editingImage.floor_level : null
                          }
                        })}
                        disabled={updateMutation.isPending}
                        className="btn-primary px-4 py-2"
                      >
                        Save Changes
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
                        selectedImage.image_type === 'scheme' ? 'bg-blue-100 text-blue-800' :
                        selectedImage.image_type === 'floor_plan' ? 'bg-green-100 text-green-800' :
                        'bg-secondary-100 text-secondary-800'
                      }`}>
                        {selectedImage.image_type === 'scheme' ? 'Development Scheme' :
                         selectedImage.image_type === 'floor_plan' ? 'Floor Plan' :
                         'Photo'}
                      </span>
                      {selectedImage.floor_level && (
                        <span className="inline-block px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">
                          {selectedImage.floor_level}
                        </span>
                      )}
                      {selectedImage.unit_type_id && (
                        <span className="inline-block px-2 py-0.5 bg-primary-100 text-primary-800 text-xs font-medium rounded">
                          Type {unitTypes.find(t => t.id === selectedImage.unit_type_id)?.label || '?'}
                        </span>
                      )}
                      <span className="text-secondary-700">
                        {selectedImage.description || 'No description'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3 items-center">
            <div className="flex-1 overflow-x-auto">
              <div className="flex gap-3 pb-2">
                {images.map((image, index) => (
                  <button
                    key={image.id}
                    draggable
                    onDragStart={() => handleThumbDragStart(index)}
                    onDragOver={(e) => handleThumbDragOver(e, index)}
                    onDragEnd={handleThumbDragEnd}
                    onClick={() => setSelectedIndex(index)}
                    className={`relative flex-shrink-0 rounded-lg overflow-hidden transition-all ${
                      selectedIndex === index
                        ? 'ring-3 ring-primary-500 ring-offset-2'
                        : 'ring-1 ring-secondary-200 hover:ring-primary-300'
                    } ${draggedThumbIndex === index ? 'opacity-50 scale-95' : ''}`}
                  >
                    <img
                      src={image.image_url}
                      alt={image.description || `Thumbnail ${index + 1}`}
                      className="w-20 h-20 object-cover"
                    />
                    {index === 0 && (
                      <div className="absolute bottom-0 left-0 right-0 bg-primary-600 text-white text-[10px] font-medium text-center py-0.5">
                        Primary
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              className={`flex-shrink-0 w-20 h-20 rounded-lg border-2 border-dashed flex items-center justify-center cursor-pointer transition-all ${
                dragOver
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-secondary-300 hover:border-primary-400 hover:bg-secondary-50'
              }`}
            >
              {uploading ? (
                <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <svg className={`w-8 h-8 ${dragOver ? 'text-primary-500' : 'text-secondary-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          <p className="text-xs text-secondary-500 text-center">
            Drag thumbnails to reorder. Drag and drop or click + to add more images.
          </p>
        </div>
      )}
    </div>
  )
}
